import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Input, Button, Table, Row, Col, Typography,
    Statistic, Form, DatePicker, InputNumber, Space,
    notification, Empty, Tag, Divider, Popconfirm, Modal, AutoComplete
} from 'antd';
import {
    SearchOutlined, PlusOutlined, DeleteOutlined,
    DollarOutlined, UserOutlined, BankOutlined,
    ClockCircleOutlined, MessageOutlined, WalletOutlined,
    ArrowRightOutlined, HistoryOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import paymentMilestoneService from '../../services/paymentMilestoneService';
import { orderService } from '../../services/orderService';

const { Title, Text } = Typography;

const PaymentMilestones = () => {
    const [orderId, setOrderId] = useState('');
    const [loading, setLoading] = useState(false);
    const [orderInfo, setOrderInfo] = useState(null);
    const [milestones, setMilestones] = useState([]);
    const [summary, setSummary] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [recentOrders, setRecentOrders] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [form] = Form.useForm();
    const [api, contextHolder] = notification.useNotification();
    const amountRef = useRef(null);

    useEffect(() => {
        if (isModalVisible) {
            setTimeout(() => {
                amountRef.current?.focus();
            }, 100);
        }
    }, [isModalVisible]);

    useEffect(() => {
        fetchRecentOrders();
    }, []);

    const fetchRecentOrders = async () => {
        try {
            const res = await orderService.getAllOrders({ limit: 12, lite: true });
            if (res.success) {
                setRecentOrders(res.data);
            }
        } catch (error) {
            console.error('Failed to fetch recent orders', error);
        }
    };

    const fetchOrderData = async (id) => {
        if (!id) return;
        setLoading(true);
        try {
            const [infoRes, milestoneRes] = await Promise.all([
                paymentMilestoneService.getOrderPaymentSummary(id),
                paymentMilestoneService.getMilestonesByOrder(id)
            ]);

            if (infoRes.success) {
                setOrderInfo(infoRes.data);
                setSummary(infoRes.data); // Summary contains orderValue, totalPaid, remainingBalance
                setOrderId(id); // Ensure state stays in sync if selected from list
            }
            if (milestoneRes.success) {
                setMilestones(milestoneRes.data);
            }
        } catch (error) {
            api.error({
                message: 'Error',
                description: 'Failed to fetch order details. Please check the Order ID.'
            });
            setOrderInfo(null);
            setMilestones([]);
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        const id = parseInt(orderId);
        if (isNaN(id)) {
            api.warning({ message: 'Invalid Search', description: 'Please select an order from the list or enter a valid Order ID' });
            return;
        }
        fetchOrderData(id);
    };

    // Debounced search logic
    useEffect(() => {
        const timer = setTimeout(() => {
            if (orderId && orderId.length >= 2 && isNaN(parseInt(orderId))) {
                performSearch(orderId);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [orderId]);

    const performSearch = async (value) => {
        try {
            const res = await orderService.getAllOrders({ search: value, limit: 10, lite: true });
            if (res.success) {
                setSearchResults(res.data.map(order => ({
                    value: order.id.toString(),
                    label: (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Text strong style={{ fontSize: '13px' }}>{order.customer?.customerName}</Text>
                                <Text type="secondary" style={{ fontSize: '11px' }}>{order.plantSizeKw}kW · {order.customer?.district}</Text>
                            </div>
                            <Tag color="blue" style={{ marginRight: 0 }}>#{order.id}</Tag>
                        </div>
                    )
                })));
            }
        } catch (error) {
            console.error('Search failed', error);
        }
    };

    const handleSearchInput = (value) => {
        setOrderId(value);
        if (value.length < 2) {
            setSearchResults([]);
        }
    };

    const onSelectOrder = (value) => {
        setOrderId(value);
        fetchOrderData(value);
    };

    const onFinish = async (values) => {
        // Validation: Positive amount
        if (values.amount <= 0) {
            notification.warning({
                message: 'Invalid Amount',
                description: 'Payment amount must be greater than 0.'
            });
            return;
        }

        // Validation: Exceeding balance
        if (values.amount > summary.remainingBalance) {
            notification.error({
                message: 'Overpayment Detected',
                description: `You cannot record a payment larger than the remaining balance (₹${summary.remainingBalance.toLocaleString()}).`
            });
            return;
        }

        try {
            const data = {
                orderId: parseInt(orderId),
                amount: values.amount,
                paymentDate: values.paymentDate.toISOString(),
                remarks: values.remarks
            };

            const res = await paymentMilestoneService.addMilestone(data);
            if (res.success) {
                api.success({ message: 'Success', description: 'Payment recorded' });
                form.resetFields();
                setIsModalVisible(false);
                fetchOrderData(orderId);
            }
        } catch (error) {
            api.error({ message: 'Error', description: error.message });
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await paymentMilestoneService.deleteMilestone(id);
            if (res.success) {
                api.success({ message: 'Success', description: 'Payment removed' });
                fetchOrderData(orderId);
            }
        } catch (error) {
            api.error({ message: 'Error', description: 'Failed to delete milestone' });
        }
    };

    const columns = [
        {
            title: 'DATE',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            render: (date) => (
                <Text style={{ fontSize: '13px', color: '#595959' }}>
                    {dayjs(date).format('DD MMM, YYYY')}
                </Text>
            )
        },
        {
            title: 'AMOUNT',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (amt) => (
                <Text strong style={{ fontSize: '14px', color: '#262626' }}>
                    ₹{amt.toLocaleString('en-IN')}
                </Text>
            )
        },
        {
            title: 'REMARK',
            dataIndex: 'remarks',
            key: 'remarks',
            render: (text) => (
                <Text type="secondary" style={{ fontSize: '13px' }}>
                    {text || '--'}
                </Text>
            )
        },
        {
            title: '',
            key: 'action',
            width: 50,
            align: 'center',
            render: (_, record) => (
                <Popconfirm
                    title="Delete payment?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Yes"
                    cancelText="No"
                    placement="left"
                >
                    <Button type="text" size="small" danger icon={<DeleteOutlined style={{ fontSize: '14px' }} />} />
                </Popconfirm>
            )
        }
    ];

    return (
        <div style={{ width: '100%', margin: '0' }}>
            {contextHolder}
            {/* Search & Quick Pick Section */}
            <div style={{
                display: 'flex',
                gap: '24px',
                marginBottom: 32,
                alignItems: 'flex-start',
                flexWrap: 'wrap'
            }}>
                {/* Search Box */}
                <div style={{
                    display: 'flex',
                    background: '#fff',
                    padding: '4px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: '1px solid #f0f0f0',
                    width: '100%',
                    maxWidth: '400px'
                }}>
                    <AutoComplete
                        options={searchResults}
                        onSelect={onSelectOrder}
                        onSearch={handleSearchInput}
                        value={orderId}
                        style={{ width: '100%' }}
                    >
                        <Input
                            placeholder="Search Customer or Order ID..."
                            variant="borderless"
                            onPressEnter={handleSearch}
                            style={{ padding: '8px 12px' }}
                        />
                    </AutoComplete>
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={handleSearch}
                        loading={loading}
                        style={{ borderRadius: '8px', height: '40px' }}
                    >
                        Find
                    </Button>
                </div>

                {/* Recent Orders Quick Pick */}
                {recentOrders.length > 0 && (
                    <div style={{ flex: 1, minWidth: '300px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <HistoryOutlined style={{ color: '#8c8c8c', fontSize: '11px' }} />
                            <Text type="secondary" style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px' }}>Recents</Text>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {recentOrders.map(order => {
                                const isSelected = orderInfo && order.id === parseInt(orderId);
                                return (
                                    <Tag
                                        key={order.id}
                                        onClick={() => fetchOrderData(order.id)}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '6px 14px',
                                            borderRadius: '8px',
                                            background: isSelected ? '#e6f7ff' : '#fff',
                                            border: isSelected ? '1px solid #91d5ff' : '1px solid #f0f0f0',
                                            transition: 'all 0.2s',
                                            userSelect: 'none',
                                            margin: 0,
                                            boxShadow: isSelected ? '0 2px 4px rgba(24, 144, 255, 0.1)' : 'none'
                                        }}
                                        hoverable="true"
                                        className={isSelected ? "" : "hover-scale"}
                                    >
                                        <Space size={6}>
                                            <Text strong style={{ fontSize: '12px', color: isSelected ? '#1890ff' : '#262626' }}>
                                                #{order.id}
                                            </Text>
                                            <Text style={{ fontSize: '12px', color: isSelected ? '#1890ff' : '#595959' }}>
                                                {order.customer?.customerName}
                                            </Text>
                                        </Space>
                                    </Tag>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {orderInfo ? (
                <div className="animate-in">
                    {/* Minimal Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 24,
                        padding: '0 4px'
                    }}>
                        <div>
                            <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Customer</Text>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Title level={3} style={{ margin: 0 }}>{orderInfo.customerName}</Title>
                                <Tag variant="filled" color={orderInfo.bankLoanYN === 'Y' ? 'blue' : 'default'} style={{ borderRadius: '4px' }}>
                                    {orderInfo.bankLoanYN === 'Y' ? 'Bank Loan' : 'Self Funded'}
                                </Tag>
                            </div>
                        </div>
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => setIsModalVisible(true)}
                            style={{ borderRadius: '8px' }}
                        >
                            Log Payment
                        </Button>
                    </div>

                    <Row gutter={[32, 32]}>
                        <Col span={18}>
                            <div style={{
                                background: '#fff',
                                border: '1px solid #f0f0f0',
                                borderRadius: '16px',
                                overflow: 'hidden'
                            }}>
                                <Table
                                    dataSource={milestones}
                                    columns={columns}
                                    rowKey="id"
                                    pagination={false}
                                    size="middle"
                                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No transactions yet" /> }}
                                />
                            </div>
                        </Col>

                        <Col span={6}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {/* Financial Stack */}
                                <div style={{
                                    background: '#fafafa',
                                    padding: '24px',
                                    borderRadius: '16px',
                                    border: '1px solid #f0f0f0'
                                }}>
                                    <Statistic
                                        title={<Text type="secondary" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>ORDER VALUE</Text>}
                                        value={summary?.orderValue || 0}
                                        prefix={<span style={{ fontSize: '16px', marginRight: 4 }}>₹</span>}
                                        styles={{ content: { fontSize: '22px', fontWeight: 600 } }}
                                    />
                                    <Divider style={{ margin: '20px 0' }} />
                                    <Statistic
                                        title={<Text type="secondary" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>TOTAL PAID</Text>}
                                        value={summary?.totalPaid || 0}
                                        prefix={<span style={{ fontSize: '16px', marginRight: 4 }}>₹</span>}
                                        styles={{ content: { fontSize: '22px', fontWeight: 600, color: '#52c41a' } }}
                                    />
                                </div>

                                <div style={{
                                    background: '#001529',
                                    padding: '28px 24px',
                                    borderRadius: '16px',
                                    color: 'white',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                }}>
                                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', letterSpacing: '0.5px' }}>REMAINING BALANCE</Text>
                                    <div style={{ fontSize: '32px', fontWeight: 700, marginTop: 4 }}>
                                        ₹{(summary?.remainingBalance || 0).toLocaleString('en-IN')}
                                    </div>
                                    <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <div style={{
                                            height: '4px',
                                            flex: 1,
                                            background: 'rgba(255,255,255,0.1)',
                                            borderRadius: '2px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                width: `${Math.min(100, ((summary?.totalPaid || 0) / (summary?.orderValue || 1)) * 100)}%`,
                                                height: '100%',
                                                background: '#52c41a'
                                            }} />
                                        </div>
                                        <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px' }}>
                                            {Math.round(((summary?.totalPaid || 0) / (summary?.orderValue || 1)) * 100)}%
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            ) : (
                !loading && (
                    <div style={{
                        marginTop: 64,
                        textAlign: 'center',
                        padding: '48px',
                        background: '#fafafa',
                        borderRadius: '24px',
                        border: '2px dashed #f0f0f0'
                    }}>
                        <WalletOutlined style={{ fontSize: '48px', color: '#bfbfbf', marginBottom: 16 }} />
                        <Title level={4} style={{ color: '#8c8c8c', margin: 0 }}>Enter Order ID to start</Title>
                        <Text type="secondary">Track partial payments and billing milestones</Text>
                    </div>
                )
            )}

            {/* Entry Modal */}
            <Modal
                title="Log New Payment"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                centered
                styles={{ content: { borderRadius: '16px' } }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ paymentDate: dayjs() }}
                    style={{ marginTop: 16 }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="paymentDate"
                                label="Date"
                                rules={[{ required: true }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="amount"
                                label="Amount"
                                rules={[{ required: true }]}
                            >
                                <InputNumber
                                    ref={amountRef}
                                    style={{ width: '100%' }}
                                    prefix="₹"
                                    placeholder="0.00"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="remarks"
                        label="Remark / Note"
                    >
                        <Input.TextArea rows={3} placeholder="Cheque no, Bank details, etc." />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                        <Button block size="large" onClick={() => setIsModalVisible(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" block size="large" htmlType="submit">
                            Save Payment
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default PaymentMilestones;
